import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from '@if/core/yup'
import { ControlledSelect } from '@if/mui'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PageSpinner from '@src/components/PageSpinner'
import { useUser } from '@src/contexts/AppContext'
import {
  useMerchantById,
  useUpdateMerchantReportSettings,
} from '@src/data/api/merchants-api/merchants-api'
import { EReportGenerationFrequency } from '@src/types/Merchant'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const reportSettingsSchema = yup.object({
  reportGenerationFrequency: yup
    .mixed<EReportGenerationFrequency>()
    .oneOf(Object.values(EReportGenerationFrequency))
    .required()
    .default(EReportGenerationFrequency.None),
  reportRecipientsEmailList: yup
    .array()
    .of(yup.string().required())
    .test(
      'all-valid-emails',
      () => 'reportSettings.invalidEmail',
      (list) => !list || list.every((e) => EMAIL_REGEX.test(e ?? '')),
    )
    .default([]),
})

type ReportSettingsFormValues = yup.InferType<typeof reportSettingsSchema>

const ReportSettingsPage = () => {
  const { t } = useTranslation()
  const user = useUser()
  const [merchant, isLoading] = useMerchantById(user?.merchantId ?? '')
  const updateSettings = useUpdateMerchantReportSettings()
  const [emailInput, setEmailInput] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)

  const formMethods = useForm<ReportSettingsFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(reportSettingsSchema),
    defaultValues: reportSettingsSchema.getDefault() as ReportSettingsFormValues,
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = formMethods

  useEffect(() => {
    if (!merchant) return
    reset({
      reportGenerationFrequency:
        merchant.reportGenerationFrequency ?? EReportGenerationFrequency.None,
      reportRecipientsEmailList: merchant.reportRecipientsEmailList ?? [],
    })
    setShowSuccess(false)
  }, [merchant, reset])

  const frequencyOptions = useMemo(
    () => [
      <MenuItem key={EReportGenerationFrequency.None} value={EReportGenerationFrequency.None}>
        {t('reportSettings.frequency.none')}
      </MenuItem>,
      <MenuItem key={EReportGenerationFrequency.Daily} value={EReportGenerationFrequency.Daily}>
        {t('reportSettings.frequency.daily')}
      </MenuItem>,
      <MenuItem key={EReportGenerationFrequency.Weekly} value={EReportGenerationFrequency.Weekly}>
        {t('reportSettings.frequency.weekly')}
      </MenuItem>,
      <MenuItem key={EReportGenerationFrequency.Monthly} value={EReportGenerationFrequency.Monthly}>
        {t('reportSettings.frequency.monthly')}
      </MenuItem>,
    ],
    [t],
  )

  const onSubmit = (data: ReportSettingsFormValues) => {
    if (!merchant) return
    setShowSuccess(false)
    updateSettings.mutate(
      {
        merchantId: merchant.id,
        dto: {
          reportGenerationFrequency: data.reportGenerationFrequency,
          reportRecipientsEmailList: data.reportRecipientsEmailList ?? [],
        },
      },
      { onSuccess: () => setShowSuccess(true) },
    )
  }

  if (isLoading || !merchant) return <PageSpinner isLoading={true} />

  const isSaving = updateSettings.isPending
  const emailErrorKey = errors.reportRecipientsEmailList?.message
  const saveDisabled = !isDirty || isSaving || !!emailErrorKey

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Card>
          <CardHeader title={t('reportSettings.title')} subheader={t('reportSettings.subtitle')} />
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <ControlledSelect
                  control={control}
                  name="reportGenerationFrequency"
                  label={t('reportSettings.frequency.label')}
                  fullWidth
                  disableEmptyOption
                  options={frequencyOptions}
                />

                <Controller
                  name="reportRecipientsEmailList"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={field.value ?? []}
                      inputValue={emailInput}
                      onInputChange={(_, v) => setEmailInput(v)}
                      onChange={(_, v) => field.onChange(v)}
                      renderValue={(value, getItemProps) =>
                        value.map((option, index) => {
                          const { key, ...itemProps } = getItemProps({ index })
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={option}
                              {...itemProps}
                            />
                          )
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('reportSettings.recipients.label')}
                          placeholder={t('reportSettings.recipients.placeholder')}
                          helperText={
                            fieldState.error
                              ? t('reportSettings.invalidEmail')
                              : t('reportSettings.recipients.helper')
                          }
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  )}
                />

                {showSuccess && <Alert severity="success">{t('reportSettings.savedSuccess')}</Alert>}
                {updateSettings.isError && (
                  <Alert severity="error">{t('reportSettings.savedError')}</Alert>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button onClick={() => reset()} disabled={!isDirty || isSaving}>
                    {t('reportSettings.reset')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={saveDisabled}
                    loading={isSaving}
                  >
                    {t('reportSettings.save')}
                  </Button>
                </Stack>

                {merchant && (
                  <Typography variant="caption" color="text.secondary">
                    {t('reportSettings.merchantContext', { name: merchant.name })}
                  </Typography>
                )}
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </FormProvider>
  )
}

export default ReportSettingsPage
